import React, { useContext, useState, useEffect } from 'react';
import {
  Typography,
  Form,
  Row,
  Col,
  Input,
  Select,
  Button,
  message,
  Tooltip,
} from 'antd';
import { Link } from 'react-router-dom';
import { SettingOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import { UserContext, FormDataContext } from '../../../contexts';
import { IAgency } from '../../../models/agency';
import './UserProfile.css';

const { Title } = Typography;

/*
 * We are currently hard-coding in the values for the office select field.
 * After MVP, this will be refactored into an API call to the server.
 */

const officeData: any = {
  CDC: ['CDC'],
  CMS: ['CMS'],
  HRSA: ['HRSA'],
  IHS: ['IHS'],
  NIH: ['NIH - NIAID', 'NIH - NIMH', 'NIH - OAR'],
  'OASH/OIDP': ['OASH/OIDP', 'OASH - Reg. 4', 'OASH - Reg. 9', 'OASH - Reg. 6'],
  OSG: ['OSG'],
  SAMHSA: ['SAMHSA'],
};

const UserProfile: React.FC = () => {
  // #region Setting Up Context And State

  const { user, setUser } = useContext(UserContext);
  const { agencies } = useContext(FormDataContext);

  /*
   * The office select options are being stored in the local component state.
   * In order to generate the select options, we're checking to make sure
   * that the user has an office associated in their attributes. If yes, we
   * set the initial state to be that. Otherwise we initiate it with an empty
   * array.
   */

  const [officeSelectOptions, setOfficeSelectOptions] = useState(
    officeData[user.attributes['custom:agency']] || [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // #endregion

  // #region UseEffect And LifeCycle Methods

  /*
   * The useEffect method that updates the current document title.
   */

  useEffect(() => {
    document.title = 'EHE Tracker - User Profile';
  }, []);

  // #endregion

  // #region Component And Form Methods

  /*
   * When the form passes client-side validation, this method will get
   * the current authenticated CognitoUser and try to update the associated
   * attributes. If there are no errors, it will update the user context
   * and display a success message.
   *
   * If there are errors, it will display the error message.
   */

  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);
      const currentUser = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(currentUser, {
        given_name: values.first,
        family_name: values.last,
        email: values.email,
        'custom:agency': values.agency,
        'custom:office': values.office,
      });
      setUser();
      setSubmitting(false);
      message.success('Your information has been updated!');
    } catch (e) {
      setSubmitting(false);
      message.error(e.message);
    }
  };

  const onFinishFailed = () => {
    message.error('Please fix the errors!');
  };

  /*
   * Whenever the current selected agency updates, this method will reset
   * the office field and check to see if the selected value exists in our
   *  officeData. If it does, it will change the officeSelectOptions state
   *  to the associated values from officeData.
   */

  const onAgencySelectChange = (value: any) => {
    form.setFieldsValue({ office: undefined });

    if (officeData[value] !== undefined) {
      setOfficeSelectOptions(officeData[value]);
    }
  };

  // #endregion

  // #region Render Section

  return (
    <Row>
      <Col span={8}>
        <Row justify="space-between" align="middle">
          <Title>User Profile</Title>
          <Tooltip title="Account Settings">
            <Link to="/profile/settings" className="UserProfile-settings-icon">
              <SettingOutlined />
            </Link>
          </Tooltip>
        </Row>
        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          scrollToFirstError
          size="large"
        >
          <Title level={4}>First Name:</Title>
          <Form.Item
            name="first"
            initialValue={user.attributes.given_name}
            rules={[
              {
                required: true,
                message: 'Please enter a value for First Name!',
              },
            ]}
          >
            <Input placeholder="First Name" />
          </Form.Item>
          <Title level={4}>Last Name:</Title>
          <Form.Item
            name="last"
            initialValue={user.attributes.family_name}
            rules={[
              {
                required: true,
                message: 'Please enter a value for Last Name!',
              },
            ]}
          >
            <Input placeholder="Last Name" />
          </Form.Item>
          <Title level={4}>Email:</Title>
          <Form.Item
            name="email"
            initialValue={user.attributes.email}
            rules={[
              {
                required: true,
                message: 'Please enter a value for email!',
              },
            ]}
          >
            <Input placeholder="Email" />
          </Form.Item>
          <Title level={4}>Agency:</Title>
          <Form.Item
            name="agency"
            initialValue={user.attributes['custom:agency']}
            rules={[
              {
                required: true,
                message: 'Please enter a value for Agency!',
              },
            ]}
          >
            <Select
              placeholder="Select an Agency"
              onChange={onAgencySelectChange}
            >
              {agencies.map((a: IAgency) => (
                <Select.Option
                  key={`${a.id}:${a.abbreviation}`}
                  value={a.abbreviation}
                >
                  {a.abbreviation}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Title level={4}>Office:</Title>
          <Form.Item
            name="office"
            initialValue={user.attributes['custom:office']}
            rules={[
              {
                required: true,
                message: 'Please enter a value for Office!',
              },
            ]}
          >
            <Select placeholder="Select an Office">
              {officeSelectOptions.map((o: any) => (
                <Select.Option key={`office:${o}`} value={o}>
                  {o}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save
          </Button>
        </Form>
      </Col>
    </Row>
  );

  // #endregion
};

export default UserProfile;
